/**
 * Variable Type Definitions
 *
 * This file contains the actual type definitions for the variable type registry.
 * Each type defines its properties with their field types.
 *
 * To add a new type:
 * 1. Add the key to VariableTypeKey enum in variable-type-registry.types.ts
 * 2. Create the definition constant here
 * 3. Add it to VARIABLE_TYPE_REGISTRY at the bottom
 */

import {
	VariableFieldType,
	VariableTypeKey,
	type VariableTypeDefinition,
	type VariableTypeProperty,
	type VariableTypeRegistry,
} from './variable-type-registry.types';

// =============================================================================
// Common Reusable Types
// =============================================================================

/**
 * Address type - postal address information
 * Used for shipping addresses, billing addresses, client addresses, etc.
 */
const ADDRESS_TYPE: VariableTypeDefinition = {
	typeKey: VariableTypeKey.ADDRESS,
	label: 'Address',
	description: 'Postal address information',
	properties: {
		addressId: {
			type: VariableFieldType.NUMBER,
			label: 'Address ID',
			description: 'Database ID of the address',
		},
		externalId: {
			type: VariableFieldType.STRING,
			label: 'External ID',
			description: 'External system ID for the address',
		},
		line1: {
			type: VariableFieldType.STRING,
			label: 'Line 1',
			description: 'Street address line 1',
		},
		line2: {
			type: VariableFieldType.STRING,
			label: 'Line 2',
			description: 'Street address line 2',
		},
		city: {
			type: VariableFieldType.STRING,
			label: 'City',
			description: 'City',
		},
		state: {
			type: VariableFieldType.STRING,
			label: 'State',
			description: 'State/province',
		},
		zip: {
			type: VariableFieldType.STRING,
			label: 'ZIP',
			description: 'Postal/ZIP code',
		},
	},
};

/**
 * Contact type - contact person information
 * Used for client contacts, order contacts, etc.
 */
const CONTACT_TYPE: VariableTypeDefinition = {
	typeKey: VariableTypeKey.CONTACT,
	label: 'Contact',
	description: 'Contact person information',
	properties: {
		contactId: {
			type: VariableFieldType.NUMBER,
			label: 'Contact ID',
			description: 'Database ID of the contact',
		},
		firstName: {
			type: VariableFieldType.STRING,
			label: 'First Name',
			description: 'Contact first name',
		},
		lastName: {
			type: VariableFieldType.STRING,
			label: 'Last Name',
			description: 'Contact last name',
		},
		email: {
			type: VariableFieldType.EMAIL,
			label: 'Email',
			description: 'Contact email address',
		},
		phone: {
			type: VariableFieldType.PHONE,
			label: 'Phone',
			description: 'Contact phone number',
		},
		title: {
			type: VariableFieldType.STRING,
			label: 'Title',
			description: 'Job title',
		},
		isPrimary: {
			type: VariableFieldType.BOOLEAN,
			label: 'Is Primary',
			description: 'Whether this is the primary contact',
		},
	},
};

// =============================================================================
// Entity Types
// =============================================================================

/**
 * Line Item type - order/quote line item
 * Used for order details, quote details, invoice lines, etc.
 */
const LINE_ITEM_TYPE: VariableTypeDefinition = {
	typeKey: VariableTypeKey.LINE_ITEM,
	label: 'Line Item',
	description: 'Order/Invoice line item',
	properties: {
		lineItemId: {
			type: VariableFieldType.NUMBER,
			label: 'Line Item ID',
			description: 'Database ID of the line item',
		},
		lineNumber: {
			type: VariableFieldType.NUMBER,
			label: 'Line Number',
			description: 'Sequential line number',
		},
		itemId: {
			type: VariableFieldType.NUMBER,
			label: 'Item ID',
			description: 'Product/item database ID',
		},
		itemExternalId: {
			type: VariableFieldType.STRING,
			label: 'Item SKU',
			description: 'Product SKU/external ID',
		},
		itemDescription: {
			type: VariableFieldType.STRING,
			label: 'Description',
			description: 'Product description',
		},
		quantity: {
			type: VariableFieldType.NUMBER,
			label: 'Quantity',
			description: 'Quantity ordered',
		},
		quantityShipped: {
			type: VariableFieldType.NUMBER,
			label: 'Qty Shipped',
			description: 'Quantity already shipped',
		},
		price: {
			type: VariableFieldType.CURRENCY,
			label: 'Unit Price',
			description: 'Price per unit',
		},
		lineTotal: {
			type: VariableFieldType.CURRENCY,
			label: 'Line Total',
			description: 'Total for this line (qty x price)',
		},
	},
};

/**
 * Order type - full order entity
 * Used when loading a complete order with all related data
 */
const ORDER_TYPE: VariableTypeDefinition = {
	typeKey: VariableTypeKey.ORDER,
	label: 'Order',
	description: 'Complete order with line items and addresses',
	properties: {
		orderId: {
			type: VariableFieldType.NUMBER,
			label: 'Order ID',
			description: 'Database ID of the order',
		},
		orderNumber: {
			type: VariableFieldType.STRING,
			label: 'Order Number',
			description: 'Display order number (externalId)',
		},
		status: {
			type: VariableFieldType.STRING,
			label: 'Status',
			description: 'Current order status code',
		},
		statusLabel: {
			type: VariableFieldType.STRING,
			label: 'Status Label',
			description: 'Human-readable status name',
		},
		date: {
			type: VariableFieldType.DATE,
			label: 'Order Date',
			description: 'Date the order was created',
		},
		customerPO: {
			type: VariableFieldType.STRING,
			label: 'Customer PO',
			description: 'Customer purchase order number',
		},
		subtotal: {
			type: VariableFieldType.CURRENCY,
			label: 'Subtotal',
			description: 'Order subtotal before tax/shipping',
		},
		freight: {
			type: VariableFieldType.CURRENCY,
			label: 'Freight',
			description: 'Shipping/freight cost',
		},
		tax: {
			type: VariableFieldType.CURRENCY,
			label: 'Tax',
			description: 'Tax amount',
		},
		discount: {
			type: VariableFieldType.CURRENCY,
			label: 'Discount',
			description: 'Discount amount',
		},
		total: {
			type: VariableFieldType.CURRENCY,
			label: 'Total',
			description: 'Order total',
		},
		clientId: {
			type: VariableFieldType.NUMBER,
			label: 'Client ID',
			description: 'Associated client ID',
		},
		clientName: {
			type: VariableFieldType.STRING,
			label: 'Client Name',
			description: 'Client company/name',
		},
		clientEmail: {
			type: VariableFieldType.EMAIL,
			label: 'Client Email',
			description: 'Primary client email',
		},
		clientPhone: {
			type: VariableFieldType.PHONE,
			label: 'Client Phone',
			description: 'Primary client phone',
		},
		salesRepId: {
			type: VariableFieldType.NUMBER,
			label: 'Sales Rep ID',
			description: 'Assigned sales rep user ID',
		},
		salesRepName: {
			type: VariableFieldType.STRING,
			label: 'Sales Rep Name',
			description: 'Name of assigned sales rep',
		},
		orderDetails: {
			type: VariableFieldType.ARRAY,
			label: 'Line Items',
			description: 'Order line items/products',
			arrayType: VariableTypeKey.LINE_ITEM,
		},
		shippingAddress: {
			type: VariableFieldType.OBJECT,
			label: 'Shipping Address',
			description: 'Order shipping/delivery address',
			objectType: VariableTypeKey.ADDRESS,
		},
		billingAddress: {
			type: VariableFieldType.OBJECT,
			label: 'Billing Address',
			description: 'Order billing address',
			objectType: VariableTypeKey.ADDRESS,
		},
		createdAt: {
			type: VariableFieldType.DATE,
			label: 'Created At',
			description: 'When the order was created',
		},
		updatedAt: {
			type: VariableFieldType.DATE,
			label: 'Updated At',
			description: 'When the order was last modified',
		},
	},
};

/**
 * Client type - full client entity
 * Used when loading a complete client with related data
 */
const CLIENT_TYPE: VariableTypeDefinition = {
	typeKey: VariableTypeKey.CLIENT,
	label: 'Client',
	description: 'Client/customer information',
	properties: {
		clientId: {
			type: VariableFieldType.NUMBER,
			label: 'Client ID',
			description: 'Database ID of the client',
		},
		name: {
			type: VariableFieldType.STRING,
			label: 'Name',
			description: 'Company/client name',
		},
		email: {
			type: VariableFieldType.EMAIL,
			label: 'Email',
			description: 'Primary email address',
		},
		phone: {
			type: VariableFieldType.PHONE,
			label: 'Phone',
			description: 'Primary phone number',
		},
		address: {
			type: VariableFieldType.STRING,
			label: 'Address',
			description: 'Primary address line 1',
		},
		city: {
			type: VariableFieldType.STRING,
			label: 'City',
			description: 'City',
		},
		state: {
			type: VariableFieldType.STRING,
			label: 'State',
			description: 'State/province',
		},
		zip: {
			type: VariableFieldType.STRING,
			label: 'ZIP',
			description: 'Postal/ZIP code',
		},
		salesRepId: {
			type: VariableFieldType.NUMBER,
			label: 'Sales Rep ID',
			description: 'Assigned sales rep user ID',
		},
		salesRepName: {
			type: VariableFieldType.STRING,
			label: 'Sales Rep Name',
			description: 'Name of assigned sales rep',
		},
		businessType: {
			type: VariableFieldType.STRING,
			label: 'Business Type',
			description: 'Type of business',
		},
		leadOrCustomer: {
			type: VariableFieldType.STRING,
			label: 'Lead/Customer',
			description: 'Whether this is a lead or customer',
		},
		salesStatus: {
			type: VariableFieldType.STRING,
			label: 'Sales Status',
			description: 'Current sales status',
		},
		createdAt: {
			type: VariableFieldType.DATE,
			label: 'Created At',
			description: 'When the client was created',
		},
	},
};

/**
 * Quote type - full quote entity
 * Used when loading a complete quote with related data
 */
const QUOTE_TYPE: VariableTypeDefinition = {
	typeKey: VariableTypeKey.QUOTE,
	label: 'Quote',
	description: 'Quote/estimate with line items',
	properties: {
		quoteId: {
			type: VariableFieldType.NUMBER,
			label: 'Quote ID',
			description: 'Database ID of the quote',
		},
		quoteNumber: {
			type: VariableFieldType.STRING,
			label: 'Quote Number',
			description: 'Display quote number',
		},
		status: {
			type: VariableFieldType.STRING,
			label: 'Status',
			description: 'Current quote status',
		},
		date: {
			type: VariableFieldType.DATE,
			label: 'Quote Date',
			description: 'Date the quote was created',
		},
		expirationDate: {
			type: VariableFieldType.DATE,
			label: 'Expiration Date',
			description: 'When the quote expires',
		},
		subtotal: {
			type: VariableFieldType.CURRENCY,
			label: 'Subtotal',
			description: 'Quote subtotal',
		},
		tax: {
			type: VariableFieldType.CURRENCY,
			label: 'Tax',
			description: 'Tax amount',
		},
		total: {
			type: VariableFieldType.CURRENCY,
			label: 'Total',
			description: 'Quote total',
		},
		clientId: {
			type: VariableFieldType.NUMBER,
			label: 'Client ID',
			description: 'Associated client ID',
		},
		clientName: {
			type: VariableFieldType.STRING,
			label: 'Client Name',
			description: 'Client company/name',
		},
		quoteDetails: {
			type: VariableFieldType.ARRAY,
			label: 'Line Items',
			description: 'Quote line items',
			arrayType: VariableTypeKey.LINE_ITEM,
		},
	},
};

/**
 * Order Sheet type — the `orderSheet` output of the Read Email (email_ingest)
 * automation step.
 *
 * Registered so the flow builder's variable picker can expand the object and
 * offer `orderSheet.lines` directly — the Product Match step consumes that
 * array. `lines` deliberately carries no `arrayType`: expanding an array to
 * its item properties would offer un-indexed paths (`orderSheet.lines.rawText`)
 * that the runtime resolver cannot resolve, so the array is the leaf here.
 */
const ORDER_SHEET_TYPE: VariableTypeDefinition = {
	typeKey: VariableTypeKey.ORDER_SHEET,
	label: 'Order Sheet',
	description: 'Structured order lines extracted from an email',
	properties: {
		lines: {
			type: VariableFieldType.ARRAY,
			label: 'Lines',
			description:
				'The extracted product lines — what the Product Match step consumes',
		},
		lineCount: {
			type: VariableFieldType.NUMBER,
			label: 'Line Count',
			description: 'How many lines were extracted',
		},
	},
};

// =============================================================================
// Variable Type Registry
// =============================================================================

/**
 * The complete registry of all variable types.
 * This is the single source of truth for type definitions.
 */
export const VARIABLE_TYPE_REGISTRY: VariableTypeRegistry = {
	[VariableTypeKey.ADDRESS]: ADDRESS_TYPE,
	[VariableTypeKey.CONTACT]: CONTACT_TYPE,
	[VariableTypeKey.LINE_ITEM]: LINE_ITEM_TYPE,
	[VariableTypeKey.ORDER]: ORDER_TYPE,
	[VariableTypeKey.CLIENT]: CLIENT_TYPE,
	[VariableTypeKey.QUOTE]: QUOTE_TYPE,
	[VariableTypeKey.ORDER_SHEET]: ORDER_SHEET_TYPE,
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get a type definition by its key.
 */
export function getVariableTypeDefinition(
	typeKey: VariableTypeKey,
): VariableTypeDefinition {
	return VARIABLE_TYPE_REGISTRY[typeKey];
}

/**
 * Get the properties for a type, resolving any type references.
 * Tracks visited types to prevent circular reference infinite loops.
 */
export function resolveTypeProperties(
	typeKey: VariableTypeKey,
	visited: Set<VariableTypeKey> = new Set(),
): Record<string, VariableTypeProperty> {
	if (visited.has(typeKey)) {
		return {};
	}

	visited.add(typeKey);

	const typeDef = VARIABLE_TYPE_REGISTRY[typeKey];

	if (typeDef == null) {
		return {};
	}

	return typeDef.properties;
}

/**
 * Check if a type key exists in the registry.
 */
export function isValidVariableTypeKey(
	typeKey: string,
): typeKey is VariableTypeKey {
	return Object.values(VariableTypeKey).includes(typeKey as VariableTypeKey);
}

/**
 * Get all available type keys.
 */
export function getAllVariableTypeKeys(): Array<VariableTypeKey> {
	return Object.values(VariableTypeKey);
}

/**
 * Get all type definitions as an array.
 */
export function getAllVariableTypeDefinitions(): Array<VariableTypeDefinition> {
	return Object.values(VARIABLE_TYPE_REGISTRY);
}
